import { isDeceasedInSource, isPlaceholderEmail } from '@dosfilos/domain';

// ─── Lo que la ficha de una persona decide, sin pantalla ────────────────────

/** Cómo llegó la persona al sistema, para la ficha.
 *  · importada (`origin`): «Importado el …», nunca una «fecha de registro»
 *    que en realidad es el día de la carga;
 *  · creada en el sistema: su fecha de alta, o null si no la tiene (la ficha
 *    escribe «—»; nunca la de hoy). */
export type Registration =
  | { kind: 'imported'; at: string }
  | { kind: 'created'; at: string | null };

export function registrationOf(person: {
  createdAt: string | null;
  origin?: { importedAt: string } | null;
}): Registration {
  if (person.origin?.importedAt) return { kind: 'imported', at: person.origin.importedAt };
  return { kind: 'created', at: person.createdAt };
}

/** El período MÁS ANTIGUO en que tiene una inscripción (cualquier estado), o
 *  null si no tiene ninguna. */
export function firstTermName(
  enrollments: ReadonlyArray<{ termId: string }>,
  terms: ReadonlyMap<string, { name: string; startDate: string }>,
): string | null {
  let first: { name: string; startDate: string } | null = null;
  for (const e of enrollments) {
    const t = terms.get(e.termId);
    if (t && (!first || t.startDate < first.startDate)) first = t;
  }
  return first?.name ?? null;
}

type PersonLike = {
  types: readonly string[];
  tags: readonly string[];
  email: string;
  authUid?: string | undefined;
};

/** Alumno o egresado: quienes tienen portal. */
export const hasStudentPortal = (p: Pick<PersonLike, 'types'>) =>
  p.types.includes('student') || p.types.includes('alumni');

/** «Dar acceso / Reenviar acceso»: a un alumno o egresado (o a quien ya tiene
 *  cuenta), nunca a un fallecido en el origen ni a un correo provisorio. Las
 *  mismas reglas aplica el servidor. */
export function canGiveAccess(p: PersonLike): boolean {
  return (
    (hasStudentPortal(p) || !!p.authUid) &&
    !isDeceasedInSource(p.tags) &&
    !isPlaceholderEmail(p.email)
  );
}

/** «Ver portal»: alumno o egresado, nunca un fallecido en el origen. */
export function canOpenPortalView(p: Pick<PersonLike, 'types' | 'tags'>): boolean {
  return hasStudentPortal(p) && !isDeceasedInSource(p.tags);
}
